import React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { 
  Plus,
  Zap,
  Search,
  Shield,
  FileWarning,
  Key,
  Upload,
  RefreshCw,
  MoreHorizontal,
  Download
} from 'lucide-react';

interface QuickAction {
  id: string;
  icon: React.ElementType;
  label: string;
  description: string;
  color: string;
  shortcut?: string;
}

interface QuickActionsProps {
  onAction: (actionId: string) => void;
}

export const QuickActions: React.FC<QuickActionsProps> = ({ onAction }) => {
  const actions: QuickAction[] = [
    {
      id: 'new_scan',
      icon: Search,
      label: 'New Scan',
      description: 'Start a new security scan',
      color: 'text-blue-500',
      // shortcut: '⌘S'
    },
    {
      id: 'add_asset',
      icon: Plus,
      label: 'Add Asset',
      description: 'Add a new asset to monitor',
      color: 'text-emerald-500',
    },
    {
      id: 'report_vulnerability',
      icon: Shield,
      label: 'Report Vulnerability',
      description: 'Report a new security issue',
      color: 'text-red-500',
    },
    {
      id: 'check_credentials',
      icon: Key,
      label: 'Check Credentials',
      description: 'Scan for exposed credentials',
      color: 'text-purple-500'
    },
    {
      id: 'data_discovery',
      icon: FileWarning,
      label: 'Data Discovery',
      description: 'Find sensitive data exposure',
      color: 'text-yellow-500'
    },
    {
      id: 'export_findings',
      icon: Download,
      label: 'Export Findings',
      description: 'Export security findings',
      color: 'text-green-500'
    }
  ];

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="p-2 rounded-lg hover:bg-[#1A1F2E] relative group">
          <Zap className="h-5 w-5 text-gray-400 group-hover:text-gray-300" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="w-80 bg-[#1A1F2E] rounded-lg border border-gray-800 shadow-xl animate-in fade-in-0 zoom-in-95"
          sideOffset={5}
          align="end"
        >
          <div className="px-3 py-2 border-b border-gray-800">
            <div className="font-medium text-sm text-white">Quick Actions</div>
          </div>

          <div className="p-1.5">
            {actions.map((action) => (
              <DropdownMenu.Item
                key={action.id}
                className="outline-none"
                onSelect={() => onAction(action.id)}
              >
                <div className="flex items-center space-x-3 px-2 py-2 rounded-md hover:bg-gray-800/50 cursor-pointer group">
                  <div className={`p-2 rounded-lg bg-gray-800/30 ${action.color}`}>
                    <action.icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-300">
                        {action.label}
                      </div>
                      {action.shortcut && (
                        <kbd className="hidden group-hover:inline-flex items-center px-2 py-1 text-xs text-gray-500 bg-gray-800/50 rounded border border-gray-700/50">
                          {action.shortcut}
                        </kbd>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {action.description}
                    </div>
                  </div>
                </div>
              </DropdownMenu.Item>
            ))}
          </div>

          <DropdownMenu.Separator className="h-px bg-gray-800 mx-1.5 my-1" />

          <div className="px-1.5 py-2">
            <DropdownMenu.Item className="flex items-center px-2 py-2 rounded-md text-sm text-gray-300 hover:bg-gray-800/50 cursor-pointer outline-none">
              <MoreHorizontal className="h-4 w-4 text-gray-400 mr-2" />
              More actions
            </DropdownMenu.Item>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};